import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { debounce, isUndefined } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { PowerPrice } from '../types'
import { TooltipProps } from 'recharts'
import { ValueType } from 'recharts/types/component/DefaultTooltipContent'
import { TFunction } from 'i18next'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { format, addMinutes, DEFAULT_HUMAN_TIME } from '@e3dc-react/shell/Libs/date'
import { UnitTypes } from 'src/Libs/types'

type HeaderValues = {
  price: string
  date: string
  timeRange: string
}

type UsePowerPriceChartHeaderValues = {
  getTooltipValues: (props: TooltipProps<ValueType, keyof PowerPrice>) => JSX.Element
  headerValues: HeaderValues
}

/*
 * a hook that returns the dpp widget modal header values to display
 */
const usePowerPriceChartHeaderValues = ({ currentPowerPriceValues }: { currentPowerPriceValues: PowerPrice }): UsePowerPriceChartHeaderValues => {
  const { t } = useCustomTranslation()
  const [tooltipValues, setTooltipValues] = useState<PowerPrice | null>(null)
  const debouncedSetTooltipValues = debounce((value: PowerPrice | null) => setTooltipValues(value), 10)
  const headerValues = useMemo(() => {
    return mapPricesToHeaderValues(tooltipValues ?? currentPowerPriceValues, t)
  }, [tooltipValues, currentPowerPriceValues]) //eslint-disable-line

  useEffect(() => {
    return () => {
      setTooltipValues(null)
    }
  }, [])

  const getTooltipValues = useCallback(
    (props: TooltipProps<ValueType, keyof PowerPrice>): JSX.Element => {
      const payload = props?.payload
      if (payload?.length === 0) {
        debouncedSetTooltipValues(null)
        return <></>
      }

      if (isUndefined(payload)) return <></>

      debouncedSetTooltipValues({ price: payload[0].payload.price, validFrom: payload[0].payload.from, validTo: payload[0].payload.to })
      return <></>
    },
    [debouncedSetTooltipValues]
  )

  return { headerValues, getTooltipValues }
}

export default usePowerPriceChartHeaderValues

const mapPricesToHeaderValues = (prices: PowerPrice, t: TFunction): HeaderValues => {
  const price = `${getLocalizedNumber(prices.price, 3)} ${UnitTypes.centsPerKiloWattHour}`.trim()
  const from = new Date(prices.validFrom)
  const to = addMinutes(from, 15)
  const timeRange = `${format(from, DEFAULT_HUMAN_TIME)} - ${format(to, DEFAULT_HUMAN_TIME)}`
  const date = format(from, t('common.format.date-fns-date'))

  return { price, date, timeRange }
}

/**
 * Sets a fallback tooltip value based on the provided time label.
 * Necessary if no data is present for the selected time range.
 *
 * @param timeLabel - The time label in "HH:mm" format based on the XAxis label
 * @returns power price
 */
export const setFallbackTooltipValue = (timeLabel?: string): PowerPrice => {
  if (!timeLabel) {
    return { price: 0, validFrom: '', validTo: '' }
  }

  const validFrom = timeLabel || ''
  const validTo = addMinutes(new Date(timeLabel), 15).toISOString()

  return {
    price: 0,
    validFrom,
    validTo,
  }
}
